/** `rule.broadcast` — the power-pill pattern: when a tagged entity raises a
 * signal (a power item is `collected`), send another signal to every active
 * entity carrying a target tag (`stun` to every enemy). The receiving rule
 * (e.g. rule.vulnerableWindow with `stun` among its hit signals) decides
 * what the signal does. */
import { id, obj, readsSignal, raisesSignal } from '../schemaBuilders.js';
import { defineModule } from '../module.js';

export type BroadcastConfig = { on: string; fromTag: string; signal: string; toTag: string };

export const broadcast = defineModule<BroadcastConfig>({
  id: 'rule.broadcast', version: 1, attach: 'global', role: 'rule',
  summary: 'When a tagged entity raises a signal, send a signal to every entity carrying a target tag (power-pill: collect → stun all enemies).',
  config: obj({ on: readsSignal(), fromTag: id(), signal: raisesSignal(), toTag: id() }),
  requires: [], events: ['broadcast'], presentation: { broadcast: { cue: 'powerup', vfx: { effect: 'flash', palette: 'slot' } } },
  proofs: ['tagged-source-reaches-every-target', 'untagged-source-ignored', 'hidden-targets-skipped'],
  animStates: () => [],
  systems: {
    // Rules: globals run before entity rules, so targets react this tick.
    rules(ctx) {
      const c = ctx.config;
      for (const s of ctx.signals(c.on)) {
        const tags = s.data.tags;
        if (!Array.isArray(tags) || !tags.includes(c.fromTag)) continue;
        const source = ctx.entity(s.source);
        for (const t of ctx.entities({ tag: c.toTag, status: 'active' })) ctx.signal(c.signal, t, source ?? null, {});
        ctx.emit('broadcast', { signal: c.signal, x: s.data.x ?? 0, y: s.data.y ?? 0, slot: source?.slot ?? 0 });
      }
    },
  },
});
